import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import {
  useAddRangeMutation,
  useGetRangeLabelsQuery,
} from 'common/api/endpoints/harvestData';
import { handleError } from 'common/api/utils/handleError';
import { DataTable, DataTableProps } from 'common/components/DataTable';
import FormPrompt from 'common/components/FormPrompt';
import { WizardNumericInput } from 'common/components/WizardControls/WizardNumericInput';
import * as notifier from 'common/services/notification';
import { orange } from 'common/styles/colors';
import {
  RangesTableRows,
  useRangesTableData,
} from 'features/settings-view/hooks/useRangesTableData';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { rangeSchema } from 'utils/schemas/settings';
import { AddSettingBtn, AddSettingForm } from '../../styles';
import { InputWrapper } from './styles';

export type RangeValues = {
  rangeStart: number | null;
  rangeEnd: number | null;
};

export const Ranges: FC = () => {
  const [isEditing, setIsEditing] = useState(false);
  const {
    data: labels,
    isLoading,
    isFetching,
    error,
  } = useGetRangeLabelsQuery();
  const [addRange, { isLoading: isSubmitting }] = useAddRangeMutation();
  const { columns, data } = useRangesTableData(labels || []);

  const minRangeStart = useMemo(
    () =>
      labels?.reduce((max, curr) => {
        const currRange = curr.miles.split('-').map(str => parseInt(str, 10));
        const nbr = currRange[1] || 0;

        return max >= nbr ? max : nbr;
      }, 0),
    [labels],
  );

  const methods = useForm<RangeValues>({
    defaultValues: {
      rangeStart: null,
      rangeEnd: null,
    },
    resolver: yupResolver(rangeSchema),
    context: { minRangeStart },
    mode: 'onBlur',
  });
  const {
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;

  const closeForm = () => {
    reset();
    setIsEditing(false);
  };

  const onSubmit = async (data: RangeValues) => {
    const dto = { miles: `${data.rangeStart}-${data.rangeEnd}` };

    try {
      await addRange(dto).unwrap();
      notifier.showSuccessMessage(`Range '${dto.miles} miles' added.`);
      closeForm();
    } catch (e) {
      handleError(e, 'Unable to add range.');
    }
  };

  const Footer = () =>
    isEditing ? (
      <FormProvider {...methods}>
        <AddSettingForm onSubmit={handleSubmit(onSubmit)}>
          <InputWrapper>
            <WizardNumericInput
              name='rangeStart'
              maxWidth='50px'
              maxHeight='22px'
              isDisabled={isSubmitting}
            />
            <span>-</span>
            <WizardNumericInput
              name='rangeEnd'
              maxWidth='50px'
              maxHeight='22px'
              isDisabled={isSubmitting}
            />
            <span>miles</span>
          </InputWrapper>
          <button type='submit' className='add-btn' disabled={isSubmitting}>
            Add Range
          </button>
          <button
            type='button'
            className='cancel-btn'
            onClick={closeForm}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </AddSettingForm>
        <FormPrompt isDirty={isDirty} isSubmitting={isSubmitting} />
      </FormProvider>
    ) : (
      <AddSettingBtn type='button' onClick={() => setIsEditing(true)}>
        <AddCircleOutlineOutlinedIcon htmlColor={orange} />
        Add New Range
      </AddSettingBtn>
    );

  const tableProps: DataTableProps<RangesTableRows, RangeValues> = {
    columns,
    data,
    isLoading,
    isFetching,
    maxTableWidth: '500px',
    FooterComponent: error ? undefined : Footer,
  };

  useEffect(() => {
    if (error) {
      handleError(error, 'Unable to fetch range labels.');
    }
  }, [error]);

  return <DataTable {...tableProps} />;
};
